import * as THREE from 'three';
import { woodFloorTexture, plasterTexture } from './textures';

// 店内の寸法（メートル）
export const ROOM = {
  width: 12, // x: -6 .. 6
  depth: 9, // z: -4.5 .. 4.5
  height: 3.2,
};

// 色（サイトのテーマ #1a3a2a 深緑 × 木目 × 生成り）
const COLORS = {
  floor: 0x5d4432,
  wall: 0xece5d8,
  accentWall: 0x1a3a2a,
  wood: 0x7a5c3e,
  woodDark: 0x4e3a28,
  counter: 0x2e241a,
};

function woodMaterial(color) {
  return new THREE.MeshStandardMaterial({ color, roughness: 0.75, metalness: 0.05 });
}

// box を作って scene に追加し、コライダー登録もする小さなヘルパー
function addBox(scene, colliders, { size, pos, material, castShadow = true, collide = true }) {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(...size), material);
  mesh.position.set(...pos);
  mesh.castShadow = castShadow;
  mesh.receiveShadow = true;
  scene.add(mesh);
  if (collide) colliders.push(new THREE.Box3().setFromObject(mesh));
  return mesh;
}

/**
 * 店内をシーンに構築する。
 * 返り値: { colliders: THREE.Box3[], productAnchors: {position, rotationY}[] }
 * productAnchors は Phase 2 で商品メッシュを置く棚上の座標。
 */
export function buildWorld(scene) {
  const colliders = [];
  const { width: W, depth: D, height: H } = ROOM;

  scene.background = new THREE.Color(0x11150f);
  scene.fog = new THREE.Fog(0x11150f, 14, 26);

  // ── 床・天井 ──
  const floor = new THREE.Mesh(
    new THREE.PlaneGeometry(W, D),
    new THREE.MeshStandardMaterial({ map: woodFloorTexture(4, 3), roughness: 0.85 })
  );
  floor.rotation.x = -Math.PI / 2;
  floor.receiveShadow = true;
  scene.add(floor);

  const ceiling = new THREE.Mesh(
    new THREE.PlaneGeometry(W, D),
    new THREE.MeshStandardMaterial({ color: 0x3a3128, roughness: 1 })
  );
  ceiling.rotation.x = Math.PI / 2;
  ceiling.position.y = H;
  scene.add(ceiling);

  // ── 壁4面（奥=アクセント深緑、他=漆喰の生成り）──
  const wallMat = new THREE.MeshStandardMaterial({ map: plasterTexture('#ece5d8', 4, 1.2), roughness: 0.95 });
  const accentMat = new THREE.MeshStandardMaterial({ map: plasterTexture('#1a3a2a', 4, 1.2), roughness: 0.9 });
  const t = 0.2; // 壁厚
  addBox(scene, colliders, { size: [W + t * 2, H, t], pos: [0, H / 2, -D / 2 - t / 2], material: accentMat, castShadow: false });
  addBox(scene, colliders, { size: [W + t * 2, H, t], pos: [0, H / 2, D / 2 + t / 2], material: wallMat, castShadow: false });
  addBox(scene, colliders, { size: [t, H, D], pos: [-W / 2 - t / 2, H / 2, 0], material: wallMat, castShadow: false });
  addBox(scene, colliders, { size: [t, H, D], pos: [W / 2 + t / 2, H / 2, 0], material: wallMat, castShadow: false });

  // ── 天井の梁＋幅木 ──
  const beamMat = woodMaterial(0x3a2c1e);
  [-3, -1, 1, 3].forEach((z) => {
    const beam = new THREE.Mesh(new THREE.BoxGeometry(W, 0.18, 0.22), beamMat);
    beam.position.set(0, H - 0.09, z);
    beam.castShadow = false;
    beam.receiveShadow = true;
    scene.add(beam);
  });
  const skirtMat = woodMaterial(0x33271b);
  [
    { size: [W, 0.12, 0.05], pos: [0, 0.06, -D / 2 + 0.025] },
    { size: [W, 0.12, 0.05], pos: [0, 0.06, D / 2 - 0.025] },
    { size: [0.05, 0.12, D], pos: [-W / 2 + 0.025, 0.06, 0] },
    { size: [0.05, 0.12, D], pos: [W / 2 - 0.025, 0.06, 0] },
  ].forEach(({ size, pos }) => {
    const skirt = new THREE.Mesh(new THREE.BoxGeometry(...size), skirtMat);
    skirt.position.set(...pos);
    skirt.castShadow = false;
    scene.add(skirt);
  });

  // ── カウンター（奥の壁の手前）──
  const counterMat = woodMaterial(COLORS.counter);
  addBox(scene, colliders, { size: [5, 1.05, 0.9], pos: [-1.5, 0.525, -3.2], material: counterMat });
  // カウンター天板（少しはみ出す明るい木）
  addBox(scene, colliders, {
    size: [5.2, 0.06, 1.05],
    pos: [-1.5, 1.08, -3.2],
    material: woodMaterial(COLORS.wood),
    collide: false,
  });

  // ── 商品棚（右の壁沿い・3段）──
  const shelfMat = woodMaterial(COLORS.woodDark);
  const shelfX = W / 2 - 0.45; // 右壁から少し内側
  const boardW = 4.2;
  const shelfHeights = [0.8, 1.35, 1.9];
  shelfHeights.forEach((y) => {
    addBox(scene, colliders, {
      size: [0.5, 0.05, boardW],
      pos: [shelfX, y, 0],
      material: shelfMat,
      collide: false,
    });
  });
  // 棚の側板2枚（見た目）＋棚全体の当たり判定は不可視のBox3ひとつで取る
  [-boardW / 2, boardW / 2].forEach((z) => {
    addBox(scene, colliders, { size: [0.5, 2.1, 0.05], pos: [shelfX, 1.05, z], material: shelfMat, collide: false });
  });
  colliders.push(
    new THREE.Box3(
      new THREE.Vector3(shelfX - 0.3, 0, -boardW / 2 - 0.1),
      new THREE.Vector3(shelfX + 0.3, 2.1, boardW / 2 + 0.1)
    )
  );

  // 商品を置くアンカー（中段 y=1.35 の板の上、通路側を向く）
  const productAnchors = [-1.3, 0, 1.3].map((z) => ({
    position: new THREE.Vector3(shelfX - 0.02, 1.35 + 0.025, z),
    rotationY: -Math.PI / 2, // 通路（-x方向）を向く
  }));

  // ── 小物: テーブル2つ＋スツール ──
  const tableMat = woodMaterial(COLORS.wood);
  [
    [-3.5, 1.8],
    [1.5, 2.2],
  ].forEach(([x, z]) => {
    addBox(scene, colliders, { size: [0.9, 0.05, 0.9], pos: [x, 0.72, z], material: tableMat, collide: false });
    addBox(scene, colliders, { size: [0.12, 0.72, 0.12], pos: [x, 0.36, z], material: woodMaterial(COLORS.woodDark) });
    [[0.75, 0], [-0.75, 0], [0, 0.75]].forEach(([dx, dz]) => {
      addBox(scene, colliders, { size: [0.35, 0.45, 0.35], pos: [x + dx, 0.225, z + dz], material: woodMaterial(COLORS.woodDark) });
    });
  });

  // ── ライティング ──
  scene.add(new THREE.AmbientLight(0xfff2df, 0.35));
  const hemi = new THREE.HemisphereLight(0xfff5e6, 0x2a2018, 0.5);
  scene.add(hemi);

  // 窓から差す光（左壁側から）
  const sun = new THREE.DirectionalLight(0xffe8c4, 1.1);
  sun.position.set(-6, 4.5, 2);
  sun.castShadow = true;
  sun.shadow.mapSize.set(1024, 1024);
  sun.shadow.camera.left = -8;
  sun.shadow.camera.right = 8;
  sun.shadow.camera.top = 6;
  sun.shadow.camera.bottom = -6;
  scene.add(sun);

  // カウンター上のペンダントライト（暖色・シェード＋コード付き）
  const pendants = [];
  const shadeMat = new THREE.MeshStandardMaterial({
    color: 0x2b2118,
    roughness: 0.6,
    metalness: 0.3,
    side: THREE.DoubleSide,
  });
  const cordMat = new THREE.MeshBasicMaterial({ color: 0x1a140e });
  [-3, -1.5, 0].forEach((x) => {
    const p = new THREE.PointLight(0xffb46b, 6, 6, 2);
    p.position.set(x, 2.4, -3.0);
    scene.add(p);
    pendants.push(p);
    const bulb = new THREE.Mesh(
      new THREE.SphereGeometry(0.05, 12, 12),
      new THREE.MeshBasicMaterial({ color: 0xffd9a0 })
    );
    bulb.position.copy(p.position);
    scene.add(bulb);
    const shade = new THREE.Mesh(new THREE.ConeGeometry(0.16, 0.16, 20, 1, true), shadeMat);
    shade.position.set(x, 2.52, -3.0);
    scene.add(shade);
    const cord = new THREE.Mesh(new THREE.CylinderGeometry(0.006, 0.006, H - 2.6, 6), cordMat);
    cord.position.set(x, (H + 2.6) / 2, -3.0);
    scene.add(cord);
  });

  // 棚を照らすスポット
  const spot = new THREE.SpotLight(0xfff0d8, 30, 10, Math.PI / 5, 0.4, 2);
  spot.position.set(3.5, 2.9, 0);
  spot.target.position.set(shelfX, 1.3, 0);
  scene.add(spot);
  scene.add(spot.target);

  return { colliders, productAnchors, pendants };
}
